import { CandidateType } from '../../../../../mirage/types';
import { RespondedCandidateItem } from './RespondedCandidateItem';
import { Select, SelectItem } from '@tremor/react';
import { useMemo, useState } from 'react';

// Define filter option types
const DATE_SORT_OPTIONS = {
  NEWEST: 'newest', // Time
  OLDEST: 'oldest',
};
// Dynamically generate these or use static lists based on expected data
const POSITION_OPTIONS = ['All', 'Frontend Developer', 'Backend Developer', 'Full Stack Developer']; // Example Positions
const CITY_OPTIONS = ['All', 'San Francisco, CA', 'New York, NY', 'London']; // Example Cities

export const RespondedCandidateList = ({
  candidates,
  showUser,
}: {
  candidates: CandidateType[];
  showUser: (candidate: CandidateType) => void;
}) => {
  // State for filters and sorting
  const [selectedDateSort, setSelectedDateSort] = useState<string>(DATE_SORT_OPTIONS.NEWEST);
  const [selectedPosition, setSelectedPosition] = useState<string>('All');
  const [selectedCity, setSelectedCity] = useState<string>('All');

  // Apply filters and sorting
  const displayCandidates = useMemo(() => {
    let filtered = [...candidates];

    // Apply position filter
    if (selectedPosition !== 'All') {
      filtered = filtered.filter(c => c.position === selectedPosition);
    }

    // Apply city filter
    if (selectedCity !== 'All') {
      filtered = filtered.filter(c => c.location === selectedCity);
    }

    // Apply date sorting (Time)
    filtered.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return selectedDateSort === DATE_SORT_OPTIONS.NEWEST ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  }, [candidates, selectedDateSort, selectedPosition, selectedCity]);

  const totalCandidates = candidates.length;

  return (
    <div className="flex w-full flex-col gap-3 px-6 pb-6">
      <p className="text-sm text-gray-600">
        Showing {displayCandidates.length} results out of {totalCandidates} responded candidates
      </p>
      <div className="flex flex-wrap justify-between gap-y-2">
        <Select
          className="w-full sm:w-[48%] md:w-[32%]"
          value={selectedDateSort}
          onValueChange={setSelectedDateSort}
        >
          <SelectItem value={DATE_SORT_OPTIONS.NEWEST}>Newest First (Time)</SelectItem>
          <SelectItem value={DATE_SORT_OPTIONS.OLDEST}>Oldest First (Time)</SelectItem>
        </Select>
        <Select
          className="w-full sm:w-[48%] md:w-[32%]"
          value={selectedPosition}
          onValueChange={setSelectedPosition}
        >
          {POSITION_OPTIONS.map(option => (
            <SelectItem key={option} value={option}>{option === 'All' ? 'All Positions' : option}</SelectItem>
          ))}
        </Select>
        <Select
          className="w-full sm:w-[48%] md:w-[32%]"
          value={selectedCity}
          onValueChange={setSelectedCity}
        >
          {CITY_OPTIONS.map(option => (
            <SelectItem key={option} value={option}>{option === 'All' ? 'All Cities' : option}</SelectItem>
          ))}
        </Select>
      </div>
      {displayCandidates.length > 0 ? (
        displayCandidates.map((candidate: CandidateType, index: number) => (
          <RespondedCandidateItem
            showUser={showUser}
            key={candidate.id || index}
            candidate={candidate}
          />
        ))
      ) : (
        <div className="py-6 text-center text-gray-500">
          No candidates match the current filters.
        </div>
      )}
    </div>
  );
};
